/**
 * Pure helpers for merging server-generated pricing adjustments into
 * promotionalDiscounts state without overriding user-owned DiscountFamily
 * selections. Used by the client App.tsx merge effect and by backend
 * integration tests so we can verify the override behavior end-to-end.
 */

/**
 * Returns the set of DiscountFamily names present as string values in the
 * given promotionalDiscounts object. Used to mark families as user-owned
 * when loaded from the DB or chosen in the UI.
 *
 * @param {Record<string, unknown> | null | undefined} discounts
 * @returns {Set<string>}
 */
export function extractFamilyKeys(discounts) {
   const out = new Set()
   if (!discounts) return out
   for (const [key, value] of Object.entries(discounts)) {
      if (typeof value === 'string' && key !== 'numberDiscountValues') {
         out.add(key)
      }
   }
   return out
}

/**
 * Merges incoming pricing adjustment data into promotionalDiscounts. Families
 * the user owns are never touched; everything else is overwritten with the
 * incoming data (and stale keys from the previous fetch are cleared first).
 *
 * @param {{
 *   prevState: Record<string, unknown> & { numberDiscountValues?: Record<string, number> },
 *   pricingData: { numberDiscountValues: Record<string, number>, discountStates: Record<string, boolean | string> },
 *   userOwnedFamilies: Set<string>,
 *   prevPricingAdjKeys: string[],
 *   discountList: Record<string, { DiscountCode?: string | null, DiscountFamily?: string | null }>,
 * }} inputs
 * @returns {{ newState: Record<string, unknown>, newPricingAdjKeys: string[] }}
 */
export function mergePricingAdjustments({
   prevState,
   pricingData,
   userOwnedFamilies,
   prevPricingAdjKeys,
   discountList,
}) {
   const allowedFamilies = new Set()
   const allowedDiscountCodes = new Set()
   const blockedDiscountCodes = new Set()
   for (const [key, value] of Object.entries(pricingData.discountStates)) {
      if (typeof value !== 'string') continue
      if (userOwnedFamilies.has(key)) {
         blockedDiscountCodes.add(value)
      } else {
         allowedFamilies.add(key)
         allowedDiscountCodes.add(value)
      }
   }

   const keyIsAllowed = (k) => {
      // User-owned family keys themselves must never be overwritten
      if (userOwnedFamilies.has(k)) return false
      if (blockedDiscountCodes.has(k)) return false
      if (allowedFamilies.has(k) || allowedDiscountCodes.has(k)) return true
      const family = discountList[k]?.DiscountFamily
      if (family && userOwnedFamilies.has(family)) return false
      return true
   }

   const updated = { ...prevState }
   const updatedNumbers = { ...(prevState?.numberDiscountValues || {}) }
   delete updated.numberDiscountValues

   for (const key of prevPricingAdjKeys) {
      delete updated[key]
      delete updatedNumbers[key]
   }

   for (const family of allowedFamilies) {
      const incomingCode = pricingData.discountStates[family]
      for (const dk of Object.keys(discountList)) {
         if (
            discountList[dk]?.DiscountFamily === family &&
            discountList[dk]?.DiscountCode !== incomingCode
         ) {
            delete updated[dk]
         }
      }
   }

   for (const [key, value] of Object.entries(pricingData.discountStates)) {
      if (keyIsAllowed(key)) updated[key] = value
   }
   for (const [key, value] of Object.entries(
      pricingData.numberDiscountValues || {},
   )) {
      if (keyIsAllowed(key)) updatedNumbers[key] = value
   }

   const written = []
   for (const key of Object.keys(pricingData.discountStates)) {
      if (keyIsAllowed(key)) written.push(key)
   }
   for (const key of Object.keys(pricingData.numberDiscountValues || {})) {
      if (keyIsAllowed(key)) written.push(key)
   }

   return {
      newState: {
         ...updated,
         numberDiscountValues: updatedNumbers,
      },
      newPricingAdjKeys: Array.from(new Set(written)),
   }
}
